<template>
    <div>
        <div class="mb-4 text-sm uppercase">Select quantity</div>

        <div v-if="showQuantitySelector">
            <DesignLabQuantitySelector
                v-model="quantity"
                :show-total="false"
                :pricing-tiers="pricingTiers"
                :minimum-qty="minimumQty as number"
                :quantity-step="quantityStep as number"
                :product-type-short-label="
                    familyProductTypeShortLabel as string
                "
                :product-type-short-label-plural="
                    familyProductTypeShortLabelPlural as string
                "
            />
        </div>

        <div v-else>Select a size above to see price.</div>

        <div class="mb-5 mt-8 flex items-end justify-between gap-3">
            <div>
                <TurnaroundTimeLine v-if="familyTurnaroundTime" class="mb-1">
                    Ships within {{ familyTurnaroundTime }}
                </TurnaroundTimeLine>
            </div>

            <div class="text-4xl font-bold leading-snug">
                {{ totalFormatted }}
            </div>
        </div>

        <div class="grid gap-4">
            <a :href="browseThemesButtonUrl" class="btn-primary">
                Browse our themes
            </a>

            <button class="btn-secondary" @click="onGoToDesignLab">
                Create your own
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import DesignLabQuantitySelector from '~/components/design-lab/components/review/DesignLabQuantitySelector.vue';
import { ProductData } from '~/types/product';
import { scrollToId } from '~/utils/helpers';
import { getPricingTierForQuantity } from '~/utils/pricingTiers';

const emit = defineEmits(['showErrors']);

const props = defineProps<{
    productType: string;
    productDatas: ProductData[];
    selectedProduct: ProductData | null;
    validationMessages: string[];
}>();

const quantityCookie = useCookie<number | undefined>(
    `design_lab_${props.productType}_quantity`,
);

const quantity = ref(props.productDatas[0].pricingTiers[0].quantityBreak);

const {
    familyProductTypeShortLabel,
    familyProductTypeShortLabelPlural,
    familyTurnaroundTime,
} = useFamilyProductDataLabels(
    computed(() => props.productDatas),
    quantity,
);

const pricingTiers = computed(() => {
    return props.selectedProduct ? props.selectedProduct.pricingTiers : [];
});

const minimumQty = computed(() => {
    return props.selectedProduct ? props.selectedProduct.minimumOrderQty : null;
});

const quantityStep = computed(() => {
    return props.selectedProduct ? props.selectedProduct.quantityStep : null;
});

const totalFormatted = computed(() => {
    const pricingTier = getPricingTierForQuantity(
        quantity.value,
        pricingTiers.value,
    );

    return pricingTier
        ? formatCurrency(quantity.value * pricingTier.price)
        : formatCurrency(0);
});

const showQuantitySelector = computed(() => {
    return (
        pricingTiers.value.length &&
        minimumQty.value &&
        quantityStep.value &&
        familyProductTypeShortLabel.value &&
        familyProductTypeShortLabelPlural.value
    );
});

const validDesignLabProductTypes = ['gusset-bag', 'tin-can', 'flat-bottom-bag'];
const browseThemesButtonUrl = computed(() => {
    return props.productType
        ? validDesignLabProductTypes.includes(
              props.productType.replace('design-lab-', ''),
          )
            ? '/design-lab/all/' + props.productType.replace('design-lab-', '')
            : '/design-lab'
        : '/design-lab';
});

const onGoToDesignLab = async () => {
    if (props.validationMessages.length || !props.selectedProduct) {
        emit('showErrors', true);

        await nextTick();
        scrollToId('configurator-error');

        return;
    }

    navigateTo(
        `/roastar-design-lab#product=${props.selectedProduct.id}&method=create`,
    );
};

watch(
    quantity,
    (newQuantity) => {
        quantityCookie.value = newQuantity;
    },
    { immediate: true },
);
</script>
